import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import RealTimeOccupancyEnforcementTooltip from "../RealTimeOccupancyEnforcementTooltip";
import { gql, useLazyQuery } from "@apollo/client";
import type { Theme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";

export const RealTimeOccupancyPageHeader: React.FC<RealTimeOccupancyPageHeaderProps> = ({ accountId }) => {
  const [getEnforcementConfig, { data: enforcementConfigData, loading: enforcementConfigLoading }] = useLazyQuery<
    GetIsAutomatedEnforcementEnabledData,
    GetIsAutomatedEnforcementEnabledVars
  >(GET_IS_AUTOMATED_ENFORCEMENT_ENABLED);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (accountId) {
      getEnforcementConfig({ variables: { accountId } });
    }
  }, [getEnforcementConfig, accountId]);

  const loading = !accountId || enforcementConfigLoading;
  const automatedEnforcementActive = enforcementConfigData?.account?.isAutomatedEnforcementEnabled ?? false;

  return loading ? (
    <Skeleton variant="rectangular" width={"100%"} height={"44px"}>
      <Box width={"100%"} height={"44px"} />
    </Skeleton>
  ) : (
    <Grid container justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h4" color="text.primary">
          Real Time Occupancy
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            gap: (theme: Theme) => theme.spacing(1.25),
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Automated Enforcement:
          </Typography>
          {!automatedEnforcementActive ? (
            <Typography variant="body1" color="error.main">
              Inactive
            </Typography>
          ) : (
            <RealTimeOccupancyEnforcementTooltip
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
            >
              <Typography variant="body1" color="success.main">
                Active
              </Typography>
            </RealTimeOccupancyEnforcementTooltip>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export const GET_IS_AUTOMATED_ENFORCEMENT_ENABLED = gql`
  query GetIsAutomatedEnforcementEnabled($accountId: uuid!) {
    account: account_by_pk(id: $accountId) {
      id
      isAutomatedEnforcementEnabled: is_automated_enforcement_enabled
    }
  }
`;

interface GetIsAutomatedEnforcementEnabledData {
  account: {
    id: string;
    isAutomatedEnforcementEnabled: boolean;
  } | null;
}

interface GetIsAutomatedEnforcementEnabledVars {
  accountId: string;
}

export interface RealTimeOccupancyPageHeaderProps {
  accountId?: string;
}

export default RealTimeOccupancyPageHeader;
